<template>
  <div class="receipt-summary-page">
    <!-- 操作与查询区 -->
    <section class="command-bar">
      <div class="command-top">
        <div class="button-row">
          <el-button type="primary" @click="openCreateModal">创建</el-button>
          <el-button :disabled="!canAudit" @click="auditSelected">审核</el-button>
          <el-button :disabled="!canDelete" @click="deleteSelected">删除</el-button>
          <el-button class="danger-btn" :disabled="!canVoid" @click="voidSelected">作废</el-button>
        </div>
        <div class="hint">{{ currentOrderText }}</div>
      </div>
      <div class="query-grid">
        <div class="field">
          <label>单据编号</label>
          <el-input v-model="queryForm.orderCode" placeholder="输入单据编号" clearable @keyup.enter.native="applyQuery" />
        </div>
        <div class="field">
          <label>汇总期间起</label>
          <el-date-picker v-model="queryForm.periodStart" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" />
        </div>
        <div class="field">
          <label>汇总期间止</label>
          <el-date-picker v-model="queryForm.periodEnd" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" />
        </div>
        <div class="field">
          <label>业务年度</label>
          <el-input v-model="queryForm.businessYear" placeholder="2026" clearable @keyup.enter.native="applyQuery" />
        </div>
        <el-button type="primary" @click="applyQuery">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </section>

    <!-- 汇总单列表 -->
    <section class="section-card">
      <div class="section-head">
        <div class="section-title">汇总单列表</div>
        <div class="tabs">
          <button
            v-for="tab in summaryTabs"
            :key="tab.value"
            type="button"
            class="tab"
            :class="{ active: summaryView === tab.value }"
            @click="changeSummaryView(tab.value)"
          >
            {{ tab.label }} <span>{{ tab.count }}</span>
          </button>
        </div>
      </div>
      <el-table
        :data="summaryList"
        class="summary-table wid100"
        :row-class-name="summaryRowClassName"
        highlight-current-row
        @row-click="handleSummaryRowClick"
        :height="summaryList.length > 0 ? 280 : 120"
        v-loading="summaryLoading"
      >
        <el-table-column label="单据状态" width="96">
          <template slot-scope="scope">
            <span class="tag" :class="statusTagClass(scope.row.status)">{{ scope.row.statusName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="orderCode" label="单据编码" width="168" show-overflow-tooltip />
        <el-table-column prop="docDate" label="单据日期" width="108">
          <template slot-scope="scope">{{ formatDate(scope.row.docDate) }}</template>
        </el-table-column>
        <el-table-column prop="summaryPeriod" label="汇总期间" width="132" show-overflow-tooltip />
        <el-table-column prop="makerName" label="制单人" width="96" show-overflow-tooltip />
        <el-table-column prop="financeOrg" label="收款财务组织" min-width="210" show-overflow-tooltip />
        <el-table-column prop="businessYear" label="业务年度" width="86" />
        <el-table-column label="汇总金额" width="118">
          <template slot-scope="scope">
            <span class="money">{{ formatMoney(scope.row.totalAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="passNcName" label="是否传递NC" width="96" />
        <el-table-column label="NC单据编号" width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="muted">{{ scope.row.ncNo || '-' }}</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="pageCount > 1"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageNo"
        :page-sizes="[30, 50]"
        :page-size="pageSize"
        layout="sizes, prev, pager, next, jumper"
        :page-count="pageCount"
      />
    </section>

    <!-- 明细数据列表 -->
    <section class="section-card">
      <div class="section-head">
        <div class="section-title">明细数据列表</div>
        <div class="section-meta">{{ detailOrderText }}</div>
      </div>
      <div class="summary-strip">
        <span>汇总期间：<strong>{{ selectedPeriodText }}</strong></span>
        <span>明细 <strong>{{ detailCount }}</strong> 条，合计 <strong>{{ formatMoney(detailAmount) }}</strong></span>
      </div>
      <el-table
        :data="detailList"
        class="detail-table wid100"
        :height="detailList.length > 0 ? 280 : 120"
        v-loading="detailLoading"
      >
        <el-table-column prop="summary" label="摘要" min-width="260" show-overflow-tooltip />
        <el-table-column prop="incomeItem" label="收支项目" width="110" show-overflow-tooltip />
        <el-table-column prop="feePeriod" label="费用期间" width="96" />
        <el-table-column prop="invoiceStatus" label="开票状态" width="96" />
        <el-table-column prop="project" label="项目" width="118" show-overflow-tooltip />
        <el-table-column label="实收总金额" width="118">
          <template slot-scope="scope">
            <span class="money">{{ formatMoney(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="settlementMethod" label="结算方式" width="92" />
        <el-table-column prop="bankAccount" label="收款银行账户" min-width="210" show-overflow-tooltip />
        <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
      </el-table>
    </section>

    <!-- 创建汇总单 -->
    <el-dialog
      title="创建汇总单"
      :visible.sync="createDialogVisible"
      width="720px"
      :close-on-click-modal="false"
      @closed="resetCreateForm"
    >
      <el-form :model="createForm" label-width="110px" class="create-form">
        <el-row :gutter="14">
          <el-col :span="12">
            <el-form-item label="单据编号">
              <el-input v-model="createForm.orderCode" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="制单人">
              <el-input v-model="createForm.makerName" readonly />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="汇总开始时间">
              <el-input
                v-if="createForm.summaryStartAuto"
                :value="formatSummaryStartDisplay(createForm.summaryStartTime)"
                readonly
              />
              <el-date-picker
                v-else
                type="date"
                placeholder="选择开始日期"
                value-format="timestamp"
                v-model="createForm.summaryStartTime"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="汇总截止时间">
              <el-date-picker
                type="date"
                placeholder="选择截止日期"
                value-format="timestamp"
                v-model="createForm.summaryEndTime"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input type="textarea" v-model="createForm.remark" :rows="4" placeholder="请输入备注" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCreate">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 实收汇总单
 */
export default {
  data() {
    return {
      gardenId: '',
      summaryLoading: false,
      detailLoading: false,
      summaryList: [],
      detailList: [],
      selectedSummary: null,
      summaryView: 'all',
      countAll: 0,
      countPending: 0,
      countPushed: 0,
      pageNo: 1,
      pageSize: 30,
      pageCount: 0,
      detailCount: 0,
      detailAmount: 0,
      queryForm: {
        orderCode: '',
        periodStart: '',
        periodEnd: '',
        businessYear: ''
      },
      createDialogVisible: false,
      createForm: {
        orderCode: '',
        makerId: '',
        makerName: '',
        summaryStartTime: '',
        summaryStartAuto: false,
        summaryEndTime: '',
        docDate: '',
        financeOrg: '',
        remark: ''
      }
    }
  },
  computed: {
    summaryTabs() {
      return [
        { label: '所有数据', value: 'all', count: this.countAll },
        { label: '待推送', value: 'pending', count: this.countPending },
        { label: '已推送', value: 'pushed', count: this.countPushed }
      ]
    },
    currentOrderText() {
      if (!this.selectedSummary) {
        return '请选择汇总单'
      }
      return `当前汇总单：${this.selectedSummary.orderCode}，状态：${this.selectedSummary.statusName}`
    },
    detailOrderText() {
      return this.selectedSummary ? `当前汇总单：${this.selectedSummary.orderCode}` : '未选择汇总单'
    },
    selectedPeriodText() {
      return this.selectedSummary ? this.selectedSummary.summaryPeriod : '-'
    },
    canAudit() {
      return this.selectedSummary && [1, 2].includes(this.selectedSummary.status)
    },
    canDelete() {
      return this.selectedSummary && this.selectedSummary.status === 1
    },
    canVoid() {
      return this.selectedSummary && this.selectedSummary.status === 3
    }
  },
  created() {
    this.gardenId = this.$store.getters.gardenId
    this.loadSummaryList()
  },
  watch: {
    '$store.state.allGardenId': function () {
      this.gardenId = this.$store.state.allGardenId
      this.selectedSummary = null
      this.clearSummaryDetail()
      this.pageNo = 1
      this.loadSummaryList()
    }
  },
  methods: {
    clearSummaryDetail() {
      this.detailList = []
      this.detailCount = 0
      this.detailAmount = 0
    },
    loadSummaryList() {
      this.clearSummaryDetail()
      this.summaryLoading = true
      const params = [
        `pageNo=${this.pageNo}`,
        `pageSize=${this.pageSize}`,
        `gardenId=${this.gardenId}`,
        `summaryView=${this.summaryView === 'all' ? '' : this.summaryView}`,
        `orderCode=${encodeURIComponent(this.queryForm.orderCode || '')}`,
        `periodStart=${this.queryForm.periodStart || ''}`,
        `periodEnd=${this.queryForm.periodEnd || ''}`,
        `businessYear=${this.queryForm.businessYear || ''}`
      ].join('&')
      this.$get(`/feeReceiptSummaryList?${params}`).then(
        (res) => {
          this.summaryLoading = false
          if (res && res.errCode === 0) {
            this.summaryList = res.list || []
            this.pageCount = res.pageCount || 0
            this.countAll = res.countAll || 0
            this.countPending = res.countPending || 0
            this.countPushed = res.countPushed || 0
            this.syncSelectedSummary()
          }
        },
        (error) => {
          this.summaryLoading = false
          this.$message.error(error)
        }
      )
    },
    loadSummaryDetail() {
      if (!this.selectedSummary || !this.selectedSummary.id) {
        this.clearSummaryDetail()
        return
      }
      this.clearSummaryDetail()
      this.detailLoading = true
      const summaryId = this.selectedSummary.id
      this.$get(`/feeReceiptSummaryDetail/${summaryId}`).then(
        (res) => {
          this.detailLoading = false
          if (!this.selectedSummary || this.selectedSummary.id !== summaryId) {
            return
          }
          if (res && res.errCode === 0) {
            this.detailList = res.list || []
            this.detailCount = res.detailCount || 0
            this.detailAmount = res.detailAmount || 0
            if (res.summary) {
              this.selectedSummary = res.summary
            }
          }
        },
        (error) => {
          this.detailLoading = false
          if (!this.selectedSummary || this.selectedSummary.id !== summaryId) {
            return
          }
          this.$message.error(error)
        }
      )
    },
    syncSelectedSummary() {
      if (!this.summaryList.length) {
        this.selectedSummary = null
        this.clearSummaryDetail()
        return
      }
      const exists = this.selectedSummary && this.summaryList.some(item => item.id === this.selectedSummary.id)
      if (!exists) {
        this.selectedSummary = this.summaryList[0]
      }
      this.loadSummaryDetail()
    },
    handleSummaryRowClick(row) {
      this.selectedSummary = row
      this.loadSummaryDetail()
    },
    summaryRowClassName({ row }) {
      if (this.selectedSummary && row.id === this.selectedSummary.id) {
        return 'selected-row'
      }
      return ''
    },
    changeSummaryView(view) {
      this.summaryView = view
      this.pageNo = 1
      this.loadSummaryList()
    },
    applyQuery() {
      this.pageNo = 1
      this.loadSummaryList()
    },
    resetQuery() {
      this.queryForm = {
        orderCode: '',
        periodStart: '',
        periodEnd: '',
        businessYear: ''
      }
      this.pageNo = 1
      this.loadSummaryList()
    },
    handleCurrentChange(val) {
      this.pageNo = val
      this.loadSummaryList()
    },
    handleSizeChange(val) {
      this.pageNo = 1
      this.pageSize = val
      this.loadSummaryList()
    },
    openCreateModal() {
      const loading = this.$loading()
      this.$get(`/feeReceiptSummaryPrepare?gardenId=${this.gardenId}`).then(
        (res) => {
          loading.close()
          if (res && res.errCode === 0) {
            const autoStartTime = this.resolveSummaryStartTimestamp(res)
            this.createForm = {
              orderCode: res.orderCode || '',
              makerId: res.makerId || this.$store.getters.staffId,
              makerName: res.makerName || this.$store.getters.nickName,
              summaryStartTime: autoStartTime || '',
              summaryStartAuto: !!autoStartTime,
              summaryEndTime: this.parseToTimestamp(res.defaultEndTime) || this.getTodayTimestamp(),
              docDate: res.docDate || '',
              financeOrg: res.financeOrg || '',
              remark: ''
            }
            this.createDialogVisible = true
          }
        },
        (error) => {
          loading.close()
          this.$message.error(error)
        }
      )
    },
    resetCreateForm() {
      this.createForm = {
        orderCode: '',
        makerId: '',
        makerName: '',
        summaryStartTime: '',
        summaryStartAuto: false,
        summaryEndTime: '',
        docDate: '',
        financeOrg: '',
        remark: ''
      }
    },
    saveCreate() {
      if (!this.createForm.orderCode || !this.createForm.summaryEndTime) {
        this.$message.warning('请填写单据编号和汇总截止日期')
        return
      }
      if (!this.createForm.summaryStartAuto && !this.createForm.summaryStartTime) {
        this.$message.warning('请选择汇总开始时间')
        return
      }
      const params = {
        gardenId: this.gardenId,
        orderCode: this.createForm.orderCode,
        summaryStartTime: this.createForm.summaryStartTime
          ? this.formatDateTime(this.createForm.summaryStartTime, false)
          : '',
        summaryEndTime: this.formatDateTime(this.createForm.summaryEndTime, true),
        docDate: this.createForm.docDate,
        makerId: this.createForm.makerId,
        makerName: this.createForm.makerName,
        financeOrg: this.createForm.financeOrg,
        remark: this.createForm.remark
      }
      const loading = this.$loading()
      this.$postJson('/feeReceiptSummary', params).then(
        (res) => {
          loading.close()
          if (res && res.errCode === 0) {
            this.$message.success('已创建汇总单')
            this.createDialogVisible = false
            this.selectedSummary = res.data || null
            this.loadSummaryList()
          } else if (res && res.errMsg) {
            this.$message.warning(res.errMsg)
          }
        },
        (error) => {
          loading.close()
          this.$message.error(error)
        }
      )
    },
    auditSelected() {
      if (!this.canAudit) {
        this.$message.warning('当前汇总单不可审核')
        return
      }
      const loading = this.$loading()
      this.$post(`/feeReceiptSummaryAudit/${this.selectedSummary.id}`).then(
        (res) => {
          loading.close()
          if (res && res.errCode === 0) {
            this.$message.success('已审核并传递 NC')
            this.selectedSummary = res.data || this.selectedSummary
            this.loadSummaryList()
          } else if (res && res.errMsg) {
            this.$message.warning(res.errMsg)
          }
        },
        (error) => {
          loading.close()
          this.$message.error(error)
        }
      )
    },
    deleteSelected() {
      if (!this.canDelete) {
        this.$message.warning('仅新增状态允许删除')
        return
      }
      this.$confirm('确认删除当前汇总单？', '提示', { type: 'warning' }).then(() => {
        const loading = this.$loading()
        this.$delete(`/feeReceiptSummary/${this.selectedSummary.id}`).then(
          (res) => {
            loading.close()
            if (res && res.errCode === 0) {
              this.$message.success('已删除汇总单')
              this.selectedSummary = null
              this.loadSummaryList()
            } else if (res && res.errMsg) {
              this.$message.warning(res.errMsg)
            }
          },
          (error) => {
            loading.close()
            this.$message.error(error)
          }
        )
      }).catch(() => {})
    },
    voidSelected() {
      if (!this.canVoid) {
        this.$message.warning('仅已推送状态允许作废')
        return
      }
      const loading = this.$loading()
      this.$post(`/feeReceiptSummaryVoid/${this.selectedSummary.id}`).then(
        (res) => {
          loading.close()
          if (res && res.errCode === 0) {
            this.$message.success('已作废汇总单')
            this.selectedSummary = res.data || this.selectedSummary
            this.loadSummaryList()
          } else if (res && res.errMsg) {
            this.$message.warning(res.errMsg)
          }
        },
        (error) => {
          loading.close()
          this.$message.error(error)
        }
      )
    },
    statusTagClass(status) {
      const map = {
        1: 'new',
        2: 'reviewed',
        3: 'pushed',
        4: 'voided'
      }
      return map[status] || 'reviewed'
    },
    formatMoney(value) {
      const num = Number(value || 0)
      return num.toLocaleString('zh-CN', {
        style: 'currency',
        currency: 'CNY',
        minimumFractionDigits: 2
      })
    },
    formatDate(value) {
      if (!value) {
        return ''
      }
      if (typeof value === 'string') {
        return value.slice(0, 10)
      }
      const date = new Date(value)
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },
    getTodayTimestamp() {
      const d = new Date()
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    },
    parseToTimestamp(dateStr) {
      if (!dateStr) {
        return ''
      }
      const datePart = String(dateStr).slice(0, 10)
      const parts = datePart.split('-')
      if (parts.length !== 3) {
        return ''
      }
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getTime()
    },
    resolveSummaryStartTimestamp(res) {
      if (!res) {
        return ''
      }
      if (res.defaultStartTime) {
        return this.parseToTimestamp(res.defaultStartTime)
      }
      // 兼容旧接口 lastEndTime：取上一张截止时间日期 +1 天
      if (res.lastEndTime) {
        const ts = this.parseToTimestamp(res.lastEndTime)
        if (!ts) {
          return ''
        }
        const date = new Date(ts)
        date.setDate(date.getDate() + 1)
        return date.getTime()
      }
      return ''
    },
    formatSummaryStartDisplay(timestamp) {
      if (!timestamp) {
        return ''
      }
      return this.formatDate(timestamp)
    },
    formatDateTime(timestamp, isEnd) {
      if (!timestamp) {
        return ''
      }
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${year}-${month}-${day} ${isEnd ? '23:59:59' : '00:00:00'}`
    }
  }
}
</script>

<style lang="scss" scoped>
.receipt-summary-page {
  --line: #dfe5eb;
  --line-strong: #c9d3dc;
  --text: #1f2933;
  --muted: #637083;
  --primary: #1769aa;
  --primary-soft: #e8f2fb;
  --success: #16875d;
  --warning: #b86b00;
  --danger: #bd3b3b;
  display: grid;
  gap: 16px;
  color: var(--text);
}

.command-bar,
.section-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  min-width: 0;
}

.command-bar {
  padding: 14px 16px;
  display: grid;
  gap: 12px;
}

.command-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.button-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.hint,
.section-meta,
.muted {
  color: var(--muted);
}

.hint {
  font-size: 13px;
}

.query-grid {
  display: grid;
  grid-template-columns: 1.25fr 1fr 1fr 0.8fr auto auto;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--line);
  align-items: end;
}

.field {
  display: grid;
  gap: 6px;
  min-width: 0;

  label {
    color: var(--muted);
    font-size: 12px;
  }
}

.section-head {
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
}

.section-title {
  font-size: 15px;
  font-weight: 700;
}

.section-meta {
  font-size: 13px;
}

.tabs {
  display: flex;
  gap: 6px;
  padding: 4px;
  background: #f3f6f8;
  border-radius: 7px;
}

.tab {
  border: 0;
  height: 32px;
  border-radius: 5px;
  padding: 0 12px;
  background: transparent;
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  cursor: pointer;

  &.active {
    color: #0f4f86;
    background: #fff;
    box-shadow: 0 1px 4px rgba(31, 41, 51, 0.08);
    font-weight: 700;
  }
}

.summary-strip {
  min-height: 44px;
  padding: 0 16px;
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--muted);
  background: #fbfcfd;
  flex-wrap: wrap;

  strong {
    color: var(--text);
  }
}

.money {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  &.new {
    color: var(--warning);
    background: #fff4df;
  }

  &.reviewed {
    color: #4d6478;
    background: #eaf0f5;
  }

  &.pushed {
    color: var(--success);
    background: #e7f6ef;
  }

  &.voided {
    color: var(--danger);
    background: #fdecec;
  }
}

.danger-btn {
  color: var(--danger);
  border-color: #e5b8b8;
}

.summary-table,
.detail-table {
  padding: 0 8px 8px;
}

.create-form {
  padding-top: 8px;
}

::v-deep .selected-row > td {
  background: var(--primary-soft) !important;
}

::v-deep .el-table th {
  color: var(--muted);
  background: #fbfcfd !important;
  font-size: 12px;
  font-weight: 700;
}

::v-deep .el-pagination {
  padding: 10px 16px 14px;
}

@media (max-width: 1200px) {
  .query-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 820px) {
  .query-grid {
    grid-template-columns: 1fr;
  }

  .section-head,
  .summary-strip,
  .command-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .tabs {
    width: 100%;
    overflow-x: auto;
  }
}
</style>
